import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Wallet, Plus, Link2, MessageSquareWarning, RefreshCw, AlertCircle, Check, X,
    Scale, EyeOff, Eye,
} from "lucide-react";
import {
    generateCharges, listCharges, setKlaerung, listRentPayments, createRentPayment,
    proposeAllocations, createCharge, listTenants,
    listAllocations, createAllocation, confirmAllocation, deleteAllocation, mietkontoSummary,
    mietkontoAbweichungen, zahlungKeineMiete,
} from "@/lib/api";
import { euro, germanDate } from "@/lib/format";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { toast } from "sonner";
import { useSchreibschutz } from "@/lib/auth";

// Die sechs Klärungsstatus des Backends, mit derselben Bedeutung. Die Reihenfolge
// ist die des Alltags: erst „ich kümmere mich", dann Fristen, dann Verzicht.
const KLAERUNG = [
    { value: "in_klaerung",   label: "In Klärung — jemand kümmert sich",        ampel: "gelb"  },
    { value: "gestundet",     label: "Gestundet — bis Datum aufgeschoben",      ampel: "gelb",  frist: true },
    { value: "ratenzahlung",  label: "Ratenzahlung — letzte Rate bis Datum",    ampel: "gelb",  frist: true },
    { value: "erlassen",      label: "Erlassen — Minderung anerkannt",          ampel: "grün", betrag: true },
    { value: "verrechnet",    label: "Verrechnet — gegen Kaution / Guthaben",   ampel: "grün", betrag: true },
    { value: "abgeschrieben", label: "Abgeschrieben — uneinbringlich",          ampel: "grün", betrag: true },
];
const KLAERUNG_BY = Object.fromEntries(KLAERUNG.map((k) => [k.value, k]));
// Sortierungen der Monatsliste. Auf Modulebene, weil es eine Konstante ist —
// im Bauteil definiert waere sie bei jedem Rendern neu und muesste als
// Abhaengigkeit des useMemo mitgefuehrt werden, das dann nie greift.
// "Offen absteigend" ist der Standard: wer aufs Mietkonto geht, will wissen,
// wo das meiste Geld fehlt, nicht was alphabetisch zuerst kommt.
// Leere Werte nach UNTEN. Eine leere Zeichenkette sortiert alphabetisch
// zuerst -- damit standen beim Sortieren nach Immobilie genau die Zeilen oben,
// die zur Frage nichts beitragen (Zahlungen auf Sammelkonten ohne Haus).
const textCmp = (a, b) => {
    const x = a || "", y = b || "";
    if (!x && !y) return 0;
    if (!x) return 1;
    if (!y) return -1;
    return x.localeCompare(y);
};

const SORTIERUNGEN = {
    offen_ab:  { label: "Offen — größter Betrag zuerst",
                 cmp: (a, b) => (b.rest_offen ?? b.offen) - (a.rest_offen ?? a.offen) },
    einheit:   { label: "Einheit A–Z",
                 cmp: (a, b) => textCmp(a.unit_label, b.unit_label) },
    immobilie: { label: "Immobilie, dann Einheit",
                 cmp: (a, b) => textCmp(a.immobilie, b.immobilie)
                     || textCmp(a.unit_label, b.unit_label) },
    mieter:    { label: "Mieter A–Z",
                 cmp: (a, b) => textCmp(a.tenant_name, b.tenant_name) },
    soll_ab:   { label: "Soll — größter Betrag zuerst",
                 cmp: (a, b) => b.soll - a.soll },
    faellig:   { label: "Fälligkeit — älteste zuerst",
                 cmp: (a, b) => textCmp(a.due_date, b.due_date) },
};

// Eigene Sortierungen je Reiter: die Rueckstandsliste hat kein Soll und keine
// Faelligkeit, die Zahlungsliste keinen Mieter. Eine gemeinsame Liste haette
// in jedem Reiter Einträge, die dort nichts sortieren.
const SORT_RUECKSTAENDE = {
    offen_ab:   { label: "Offen — größter Betrag zuerst",
                  cmp: (a, b) => b.offen - a.offen },
    monate_ab:  { label: "Monate — längster Rückstand zuerst",
                  cmp: (a, b) => b.monate - a.monate },
    aeltester:  { label: "Ältester offener Monat zuerst",
                  cmp: (a, b) => textCmp((a.perioden || [])[0], (b.perioden || [])[0]) },
    immobilie:  { label: "Immobilie, dann Einheit",
                  cmp: (a, b) => textCmp(a.immobilie, b.immobilie)
                      || textCmp(a.unit_label, b.unit_label) },
    einheit:    { label: "Einheit A–Z",
                  cmp: (a, b) => textCmp(a.unit_label, b.unit_label) },
    mieter:     { label: "Mieter A–Z",
                  cmp: (a, b) => textCmp(a.tenant_name, b.tenant_name) },
};

const SORT_ZAHLUNGEN = {
    datum_ab:   { label: "Buchungstag — neueste zuerst",
                  cmp: (a, b) => textCmp(b.booking_date, a.booking_date) },
    datum_auf:  { label: "Buchungstag — älteste zuerst",
                  cmp: (a, b) => textCmp(a.booking_date, b.booking_date) },
    betrag_ab:  { label: "Betrag — größter zuerst",
                  cmp: (a, b) => b.amount - a.amount },
    frei_ab:    { label: "Freier Rest — größter zuerst",
                  cmp: (a, b) => b.rest - a.rest },
    immobilie:  { label: "Immobilie, dann Einheit",
                  cmp: (a, b) => textCmp(a.immobilie, b.immobilie)
                      || textCmp(a.einheit, b.einheit) },
    absender:   { label: "Absender A–Z",
                  cmp: (a, b) => textCmp(a.counterparty_name, b.counterparty_name) },
};

// Suchfelder auf Modulebene: useFulltextFilter fuehrt das Array als
// Abhaengigkeit seines useMemo. Im Bauteil erzeugt waere es bei jedem Rendern
// neu und der Filter rechnete jedes Mal von vorn.
const SUCHE_MONAT = ["tenant_name", "unit_label", "immobilie", "period", "grund"];
const SUCHE_RUECKSTAENDE = ["tenant_name", "unit_label", "immobilie", "entitaet",
                            (r) => (r.perioden || []).join(" ")];
const SUCHE_ZAHLUNGEN = ["counterparty_name", "reference", "account_iban", "konto",
                         "einheit", "immobilie", "entitaet", "booking_date"];

// Adresse zuerst, dann die Lage im Haus. "WE 04 — 3.OG" allein ist ueber 34
// Immobilien nicht eindeutig -- und gesucht wird nach der Adresse. Die Einheit
// bleibt in derselben Zeile: Mieter wechseln, die Einheit bleibt.
const EinheitZelle = ({ immobilie, einheit, zusatz }) => (
    <div>
        {/* Nichts bekannt heisst schlicht "—". "ohne Immobilie" steht nur da,
            wo eine Einheit ohne Haus haengt -- DAS ist eine Luecke in den Daten.
            Bei Zahlungen auf Sammelkonten ist die leere Zelle dagegen der
            Normalfall, und 137 Mal "ohne Immobilie" waere nur Laerm. */}
        {!immobilie && !einheit ? (
            <span className="text-gray-400">—</span>
        ) : (
            <div className="font-medium text-slate-900">
                {immobilie || "ohne Immobilie"}
                {einheit ? <span className="text-slate-600"> · {einheit}</span> : null}
            </div>
        )}
        {zusatz ? <div className="text-xs text-gray-500">{zusatz}</div> : null}
    </div>
);

// Auswahllisten aus den Daten selbst, nicht aus einer festen Liste: sonst
// stehen Entitaeten im Filter, die in diesem Reiter nicht vorkommen, und neue
// fehlen.
const auswahlAus = (liste) => {
    const nimm = (feld) => [...new Set((liste || []).map((x) => x[feld]).filter(Boolean))].sort();
    return { entitaeten: nimm("entitaet"), immobilien: nimm("immobilie"), typen: nimm("typ") };
};

const passt = (x, w) =>
    (w.entitaet === "alle" || x.entitaet === w.entitaet)
    && (w.immobilie === "alle" || x.immobilie === w.immobilie)
    && (w.typ === "alle" || x.typ === w.typ);

// Ein eigener Filtersatz je Reiter. Geteilte Filter waeren bequem, aber ein aus
// dem Monatsreiter mitgenommener Filter, der in den Zahlungen nichts trifft,
// sieht aus wie eine leere Liste -- und man sucht den Fehler in den Daten.
const useFilterSatz = () => {
    const [entitaet, setEntitaet] = useState("alle");
    const [immobilie, setImmobilie] = useState("alle");
    const [typ, setTyp] = useState("alle");
    return {
        werte: { entitaet, immobilie, typ },
        setzen: { entitaet: setEntitaet, immobilie: setImmobilie, typ: setTyp },
        zuruecksetzen: () => { setEntitaet("alle"); setImmobilie("alle"); setTyp("alle"); },
        aktiv: entitaet !== "alle" || immobilie !== "alle" || typ !== "alle",
    };
};

const FilterLeiste = ({ prefix, platzhalter, query, setQuery, auswahl, satz,
                       sortierungen, sortierung, setSortierung, matches, total,
                       label, labelSingular, extraAktiv, extraZuruecksetzen,
                       children }) => (
    <div className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
        <SearchBox value={query} onChange={setQuery} testid={`${prefix}-search`}
                   placeholder={platzhalter} className="w-[280px]" />
        {children}
        {auswahl.entitaeten.length > 1 && (
            <SuchAuswahl wert={satz.werte.entitaet} onWaehlen={satz.setzen.entitaet}
                optionen={[
                    { wert: "alle", text: "Alle Entitäten" },
                    ...auswahl.entitaeten.map((e) => ({ wert: e, text: e })),
                ]}
                className="w-[190px]"
            />
        )}
        {auswahl.immobilien.length > 1 && (
            <SuchAuswahl wert={satz.werte.immobilie} onWaehlen={satz.setzen.immobilie}
                optionen={[
                    { wert: "alle", text: "Alle Immobilien" },
                    ...auswahl.immobilien.map((i) => ({ wert: i, text: i })),
                ]}
                className="w-[210px]"
            />
        )}
        {auswahl.typen.length > 1 && (
            <SuchAuswahl wert={satz.werte.typ} onWaehlen={satz.setzen.typ}
                optionen={[
                    { wert: "alle", text: "Alle Typen" },
                    ...auswahl.typen.map((t) => ({ wert: t, text: t })),
                ]}
                className="w-[150px]"
            />
        )}
        <SuchAuswahl wert={sortierung} onWaehlen={setSortierung}
            optionen={[
                ...Object.entries(sortierungen).map(([k, v]) => ({ wert: k, text: v.label })),
            ]}
            className="w-[240px]"
        />
        <MatchCount matches={matches} total={total} label={label} labelSingular={labelSingular} />
        {(satz.aktiv || extraAktiv) && (
            <Button variant="ghost" size="sm" className="h-8 text-xs"
                    data-testid={`${prefix}-filter-zuruecksetzen`}
                    onClick={() => { satz.zuruecksetzen(); extraZuruecksetzen?.(); }}>
                <X size={13} className="mr-1" /> Filter zurücksetzen
            </Button>
        )}
    </div>
);


const heuteMonat = () => new Date().toISOString().slice(0, 7);

const AmpelPunkt = ({ ampel, title }) => {
    const farbe = ampel === "gruen" ? "bg-emerald-500"
        : ampel === "gelb" ? "bg-amber-500" : "bg-rose-500";
    return (
        <span className="inline-flex items-center gap-1.5" title={title}>
            <span className={`w-2.5 h-2.5 rounded-full ${farbe} shrink-0`} />
            <span className="text-xs text-gray-600">
                {ampel === "gruen" ? "erledigt" : ampel === "gelb" ? "in Arbeit" : "offen"}
            </span>
        </span>
    );
};

const Kachel = ({ label, wert, ton = "neutral", fuss, testid }) => {
    const farbe = ton === "rot" ? "text-rose-600" : ton === "gelb" ? "text-amber-600"
        : ton === "gruen" ? "text-emerald-600" : "text-slate-900";
    return (
        <div className="bg-white border border-gray-200 rounded-md p-4" data-testid={testid}>
            {/* Beschriftung und Betrag duerfen die Kachel nicht sprengen: bei
                1100 px Fensterbreite stand "24.984,00 EUR" 33 px daneben. Die
                Schriftgroesse richtet sich deshalb nach der Laenge. */}
            <div className="kachel-label uppercase text-gray-500" title={label}>{label}</div>
            <div className={`mt-2 font-bold tabular-nums break-words ${farbe}
                             ${String(wert).length > 9 ? "text-lg" : String(wert).length > 6 ? "text-xl" : "text-2xl"}`}>
                {wert}
            </div>
            {fuss && <div className="mt-1 text-[11px] text-gray-400 leading-snug">{fuss}</div>}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Klärung setzen — das Feld, mit dem eine erklärte Abweichung aus der
// Arbeitsliste verschwindet, ohne dass man sie vergisst.
// ---------------------------------------------------------------------------
const KlaerungDialog = ({ charge, open, onOpenChange, onSaved }) => {
    const [status, setStatus] = useState("in_klaerung");
    const [grund, setGrund] = useState("");
    const [betrag, setBetrag] = useState("");
    const [frist, setFrist] = useState("");
    const [notiz, setNotiz] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const k = charge?.klaerung;
        setStatus(k?.status || "in_klaerung");
        setGrund(k?.grund || "");
        setBetrag(k?.betrag_anerkannt ?? "");
        setFrist(k?.faellig_bis || "");
        setNotiz(k?.notiz || "");
    }, [open, charge]);

    const def = KLAERUNG_BY[status] || {};

    const speichern = async () => {
        if (!grund.trim()) { toast.error("Ein Grund ist Pflicht."); return; }
        if (def.frist && !frist) { toast.error("Dieser Status braucht ein Datum."); return; }
        setSaving(true);
        try {
            const res = await setKlaerung(charge.id, {
                version: charge.version,
                status, grund: grund.trim(),
                betrag_anerkannt: def.betrag && betrag !== "" ? Number(betrag) : null,
                faellig_bis: def.frist ? frist : null,
                notiz: notiz.trim() || null,
            });
            toast.success(`Gespeichert — Ampel: ${res.ampel}`);
            onSaved(res);
            onOpenChange(false);
        } catch (e) {
            if (e?.response?.status !== 409) {
                toast.error("Nicht gespeichert", {
                    description: typeof e?.response?.data?.detail === "string"
                        ? e.response.data.detail : "Bitte Eingaben prüfen.",
                });
            }
        } finally { setSaving(false); }
    };

    const entfernen = async () => {
        setSaving(true);
        try {
            const res = await setKlaerung(charge.id, null);
            toast.success("Klärung entfernt — Position ist wieder offen");
            onSaved(res);
            onOpenChange(false);
        } finally { setSaving(false); }
    };

    if (!charge) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Abweichung begründen</DialogTitle>
                    <DialogDescription>
                        {charge.tenant_name} · {charge.period} · offen {euro(charge.offen)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label>Status</Label>
                        <SuchAuswahl wert={status} onWaehlen={setStatus}
                            optionen={[
                                ...KLAERUNG.map((k) => ({ wert: k.value, text: k.label })),
                            ]}
                            testId="klaerung-status"
                        />
                        <div className="mt-1 text-[11px] text-gray-500">
                            Ampel danach: <b>{def.ampel}</b>
                            {def.betrag && " — der anerkannte Betrag reduziert die Forderung"}
                        </div>
                    </div>

                    <div>
                        <Label>Grund <span className="text-rose-600">*</span></Label>
                        <Input value={grund} onChange={(e) => setGrund(e.target.value)}
                               placeholder="z.B. Mietminderung wegen Heizungsausfall anerkannt"
                               data-testid="klaerung-grund" />
                    </div>

                    {def.betrag && (
                        <div>
                            <Label>Anerkannter Betrag</Label>
                            <Input type="number" step="0.01" value={betrag}
                                   onChange={(e) => setBetrag(e.target.value)}
                                   placeholder={`leer = kompletter offener Betrag (${euro(charge.offen)})`}
                                   data-testid="klaerung-betrag" />
                            <div className="mt-1 text-[11px] text-gray-500">
                                Deckt der Betrag weniger als offen ist, bleibt die Position rot —
                                mit dem Rest als Forderung.
                            </div>
                        </div>
                    )}

                    {def.frist && (
                        <div>
                            <Label>Fällig bis <span className="text-rose-600">*</span></Label>
                            <Input type="date" value={frist} onChange={(e) => setFrist(e.target.value)}
                                   data-testid="klaerung-frist" />
                            <div className="mt-1 text-[11px] text-gray-500">
                                Bis dahin gelb, danach wieder rot.
                            </div>
                        </div>
                    )}

                    <div>
                        <Label>Notiz</Label>
                        <Textarea rows={2} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    {charge.klaerung && (
                        <Button variant="outline" onClick={entfernen} disabled={saving}
                                data-testid="klaerung-entfernen">
                            <X size={14} className="mr-1.5" /> Klärung entfernen
                        </Button>
                    )}
                    <Button onClick={speichern} disabled={saving}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="klaerung-speichern">
                        Speichern
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Zahlung zuordnen
// ---------------------------------------------------------------------------
const ZuordnenDialog = ({ charge, open, onOpenChange, onSaved }) => {
    const [payments, setPayments] = useState([]);
    const [bestehend, setBestehend] = useState([]);
    const [gewaehlt, setGewaehlt] = useState(null);
    const [betrag, setBetrag] = useState("");
    const [saving, setSaving] = useState(false);

    const laden = useCallback(() => {
        if (!charge) return;
        listRentPayments({ nur_offene: true }).then(setPayments).catch(() => setPayments([]));
        listAllocations({ charge_id: charge.id }).then(setBestehend).catch(() => setBestehend([]));
    }, [charge]);

    useEffect(() => {
        if (!open) return;
        setGewaehlt(null); setBetrag("");
        laden();
    }, [open, laden]);

    // Der Gegenpart zum Verwerfen. Bis zum 10.09.2026 fehlte er in der
    // Oberfläche: „Zahlungen zuordnen" schrieb 18 Vorschläge, entfernen ging,
    // bestätigen nicht — und ohne Bestätigung zählt eine Zuordnung nicht gegen
    // das Soll. Die Ampel wäre also nie grün geworden. Die Route gab es die
    // ganze Zeit, nur der Knopf fehlte.
    const bestaetigen = async (allocId) => {
        setSaving(true);
        try {
            await confirmAllocation(allocId);
            const frisch = await listCharges({ period: charge.period });
            const aktualisiert = frisch.find((c) => c.id === charge.id);
            toast.success("Zuordnung bestätigt — sie zählt jetzt gegen das Soll");
            if (aktualisiert) onSaved(aktualisiert);
            laden();
        } finally { setSaving(false); }
    };

    // Eine Fehlzuordnung muss zurücknehmbar sein — ohne das wäre der einzige Weg
    // ein Aufruf der Schnittstelle von Hand.
    const loesen = async (allocId) => {
        setSaving(true);
        try {
            await deleteAllocation(allocId);
            const frisch = await listCharges({ period: charge.period });
            const aktualisiert = frisch.find((c) => c.id === charge.id);
            toast.success("Zuordnung entfernt");
            if (aktualisiert) onSaved(aktualisiert);
            laden();
        } finally { setSaving(false); }
    };

    const waehlen = (p) => {
        setGewaehlt(p);
        // Vorbelegung mit dem, was beide Seiten hergeben — der häufigste Fall
        // soll kein Rechnen erfordern.
        setBetrag(String(Math.min(p.rest, charge?.offen ?? p.rest).toFixed(2)));
    };

    const speichern = async () => {
        if (!gewaehlt) { toast.error("Bitte eine Zahlung auswählen."); return; }
        setSaving(true);
        try {
            const res = await createAllocation({
                payment_id: gewaehlt.id, charge_id: charge.id,
                amount: betrag === "" ? null : Number(betrag),
            });
            toast.success(`Zugeordnet — Ampel: ${res.charge.ampel}`);
            onSaved(res.charge);
            onOpenChange(false);
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht zugeordnet", {
                description: typeof d === "string" ? d : d?.error || "Bitte Betrag prüfen.",
            });
        } finally { setSaving(false); }
    };

    if (!charge) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Zahlung zuordnen</DialogTitle>
                    <DialogDescription>
                        {charge.tenant_name} · {charge.period} · Soll {euro(charge.soll)},
                        noch offen {euro(charge.offen)}
                    </DialogDescription>
                </DialogHeader>

                {payments.length === 0 ? (
                    <div className="py-10 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-md">
                        Keine Zahlung mit freiem Restbetrag vorhanden.
                        <div className="mt-1 text-xs">Erst im Reiter „Zahlungen" erfassen oder einlesen.</div>
                    </div>
                ) : (
                    <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                        {payments.map((p) => (
                            <button key={p.id} type="button" onClick={() => waehlen(p)}
                                    data-testid={`zuordnen-zahlung-${p.id}`}
                                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors
                                        ${gewaehlt?.id === p.id ? "bg-slate-50 ring-1 ring-inset ring-slate-300" : ""}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="text-sm font-medium text-slate-900">
                                        {germanDate(p.booking_date)} · {euro(p.amount)}
                                    </span>
                                    <span className="text-xs font-mono tabular-nums text-emerald-700">
                                        frei {euro(p.rest)}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                    {p.counterparty_name || "ohne Absender"}
                                    {p.reference ? ` · ${p.reference}` : ""}
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {bestehend.length > 0 && (
                    <div>
                        <Label className="text-[11px] text-gray-500">Bereits zugeordnet</Label>
                        <div className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100">
                            {bestehend.map((a) => (
                                <div key={a.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                                    <span className="font-mono tabular-nums">{euro(a.amount)}</span>
                                    <span className="text-xs text-gray-500 flex-1 truncate">
                                        {a.status === "bestaetigt" ? "bestätigt" : "Vorschlag"}
                                        {a.match_reason ? ` · ${a.match_reason}` : ""}
                                    </span>
                                    {a.status !== "bestaetigt" && (
                                        <Button variant="ghost" size="sm"
                                                className="h-7 px-2 text-emerald-700 hover:text-emerald-800"
                                                onClick={() => bestaetigen(a.id)} disabled={saving}
                                                data-testid={`alloc-bestaetigen-${a.id}`}
                                                title="Vorschlag bestätigen — erst dann zählt er gegen das Soll">
                                            <Check size={13} />
                                        </Button>
                                    )}
                                    <Button variant="ghost" size="sm" className="h-7 px-2"
                                            onClick={() => loesen(a.id)} disabled={saving}
                                            data-testid={`alloc-loesen-${a.id}`}
                                            title={a.status === "bestaetigt"
                                                ? "Zuordnung entfernen"
                                                : "Vorschlag ablehnen"}>
                                        <X size={13} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {gewaehlt && (
                    <div>
                        <Label>Betrag</Label>
                        <Input type="number" step="0.01" value={betrag}
                               onChange={(e) => setBetrag(e.target.value)} data-testid="zuordnen-betrag" />
                        <div className="mt-1 text-[11px] text-gray-500">
                            Teilbeträge sind erlaubt: eine Zahlung kann mehrere Monate abdecken,
                            ein Monat aus mehreren Zahlungen bestehen.
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button onClick={speichern} disabled={saving || !gewaehlt}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="zuordnen-speichern">
                        Zuordnen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Zahlung von Hand erfassen
// ---------------------------------------------------------------------------
// Leerformular als Funktion ausserhalb der Komponente: als Objektliteral im
// Rumpf waere es bei jedem Render neu und damit eine wechselnde Abhaengigkeit
// des useEffect. Das Datum muss beim Oeffnen frisch sein, deshalb eine Funktion
// und keine Konstante.
const leeresZahlungsformular = () => ({
    account_iban: "", booking_date: new Date().toISOString().slice(0, 10),
    amount: "", counterparty_name: "", reference: "", notes: "",
});

const ZahlungDialog = ({ open, onOpenChange, onSaved }) => {
    const [form, setForm] = useState(leeresZahlungsformular);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (open) setForm(leeresZahlungsformular()); }, [open]);

    const speichern = async () => {
        if (!form.account_iban.trim()) { toast.error("Auf welches Konto kam der Betrag?"); return; }
        if (!form.amount) { toast.error("Betrag fehlt."); return; }
        setSaving(true);
        try {
            const res = await createRentPayment({
                ...form, amount: Number(form.amount), source: "manual",
                account_iban: form.account_iban.replace(/\s+/g, "").toUpperCase(),
            });
            toast.success(res.status === "bereits_vorhanden" ? "War schon erfasst" : "Zahlung erfasst");
            onSaved();
            onOpenChange(false);
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Zahlung erfassen</DialogTitle>
                    <DialogDescription>
                        Von Hand — für den Auszugsimport später kommt derselbe Weg automatisch.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label>Eigenes Konto (IBAN) <span className="text-rose-600">*</span></Label>
                        <Input value={form.account_iban} placeholder="DE.."
                               onChange={(e) => setForm({ ...form, account_iban: e.target.value })}
                               data-testid="zahlung-iban" />
                        <div className="mt-1 text-[11px] text-gray-500">
                            Das Konto, auf dem der Betrag einging — bei einem Konto pro Einheit
                            ist das später der Schlüssel zur automatischen Zuordnung.
                        </div>
                    </div>
                    <div>
                        <Label>Buchungstag</Label>
                        <Input type="date" value={form.booking_date}
                               onChange={(e) => setForm({ ...form, booking_date: e.target.value })} />
                    </div>
                    <div>
                        <Label>Betrag <span className="text-rose-600">*</span></Label>
                        <Input type="number" step="0.01" value={form.amount}
                               onChange={(e) => setForm({ ...form, amount: e.target.value })}
                               data-testid="zahlung-betrag" />
                    </div>
                    <div className="col-span-2">
                        <Label>Absender</Label>
                        <Input value={form.counterparty_name}
                               onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <Label>Verwendungszweck</Label>
                        <Input value={form.reference}
                               onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={speichern} disabled={saving}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="zahlung-speichern">
                        Erfassen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Soll erzeugen — Trockenlauf zuerst, wie im Backend
// ---------------------------------------------------------------------------
// Nebenkostenabrechnung als eigene Forderung erfassen (Auftrag 6).
//
// Das Vorzeichen traegt die Richtung, und deshalb steht es hier nicht als
// Minuszeichen im Betragsfeld, sondern als Auswahl: Wer "-240,97" tippt,
// meint nicht immer, was das Vorzeichen bedeutet. Gefragt wird im Klartext,
// wer wem schuldet; das Vorzeichen setzt die Oberflaeche.
// Der Server antwortet mit {error, hinweis} oder mit einer Zeichenkette. Ohne
// diese Aufloesung steht im Hinweisfeld "[object Object]" -- und der Grund,
// warum etwas nicht ging, ist genau das, was man wissen will.
const fehlertext = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.error) return [d.error, d.hinweis].filter(Boolean).join(" — ");
    return e?.message || "Unbekannter Fehler";
};

const RICHTUNG = [
    { value: "nachzahlung", label: "Nachzahlung — der Mieter zahlt", faktor: 1 },
    { value: "guthaben",    label: "Guthaben — wir erstatten",       faktor: -1 },
];

const LEERE_ABRECHNUNG = {
    tenant_id: "", richtung: "nachzahlung", betrag: "",
    abrechnungsjahr: String(new Date().getFullYear() - 1),
    period: "", due_date: "", notes: "",
};

const AbrechnungDialog = ({ open, onOpenChange, onDone, period }) => {
    const [f, setF] = useState(LEERE_ABRECHNUNG);
    const [mieter, setMieter] = useState([]);
    const [vorschau, setVorschau] = useState(null);
    const [busy, setBusy] = useState(false);
    const setFeld = (k, v) => { setF((alt) => ({ ...alt, [k]: v })); setVorschau(null); };

    useEffect(() => {
        if (!open) { setF({ ...LEERE_ABRECHNUNG, period }); setVorschau(null); return; }
        setF({ ...LEERE_ABRECHNUNG, period });
        // ALLE Mietverhaeltnisse, nicht nur die aktiven: Die Abrechnung kommt
        // Monate nach dem Auszug: wer ausgezogen ist, fehlte sonst genau dann,
        // wenn er gebraucht wird.
        listTenants().then(setMieter).catch(() => toast.error("Mieter nicht geladen"));
    }, [open, period]);

    const nutzlast = () => {
        const faktor = RICHTUNG.find((r) => r.value === f.richtung)?.faktor ?? 1;
        return {
            tenant_id: f.tenant_id, art: "nk_abrechnung", period: f.period,
            soll_total: faktor * Math.abs(parseFloat(String(f.betrag).replace(",", ".")) || 0),
            abrechnungsjahr: f.abrechnungsjahr || null,
            due_date: f.due_date || null, notes: f.notes || null,
        };
    };

    const fehlt = !f.tenant_id || !f.period
        || !(Math.abs(parseFloat(String(f.betrag).replace(",", ".")) || 0) > 0);

    const pruefen = async () => {
        setBusy(true);
        try { setVorschau(await createCharge(nutzlast(), { apply: false })); }
        catch (e) { toast.error(fehlertext(e)); }
        finally { setBusy(false); }
    };

    const schreiben = async () => {
        setBusy(true);
        try {
            const r = await createCharge(nutzlast(), { apply: true });
            toast.success(`Abrechnung angelegt — ${euro(r.charge.soll_total)}`,
                          { description: r.mieter });
            onDone(); onOpenChange(false);
        } catch (e) { toast.error(fehlertext(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="abrechnung-dialog">
                <DialogHeader>
                    <DialogTitle>Nebenkostenabrechnung erfassen</DialogTitle>
                    <DialogDescription>
                        Das Ergebnis einer anderswo erstellten Abrechnung — gerechnet wird
                        hier nichts. Die Position bekommt eine eigene Fälligkeit und läuft
                        nicht in die Mietampel.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs text-gray-500">Mietverhältnis</Label>
                        <SuchAuswahl
                            wert={f.tenant_id} onWaehlen={(v) => setFeld("tenant_id", v)}
                            platzhalter="Mieter suchen …"
                            optionen={[...mieter]
                                .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""))
                                .map((t) => ({
                                    wert: t.id,
                                    text: t.status === "aktiv" ? t.full_name
                                        : `${t.full_name} (${t.status})`,
                                }))}
                            testId="abrechnung-mieter" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs text-gray-500">Richtung</Label>
                            <Select value={f.richtung} onValueChange={(v) => setFeld("richtung", v)}>
                                <SelectTrigger data-testid="abrechnung-richtung">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {RICHTUNG.map((r) => (
                                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Betrag</Label>
                            <Input value={f.betrag} onChange={(e) => setFeld("betrag", e.target.value)}
                                   placeholder="317,54" inputMode="decimal"
                                   data-testid="abrechnung-betrag" />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs text-gray-500">Abrechnungsjahr</Label>
                            <Input value={f.abrechnungsjahr} placeholder="2024"
                                   onChange={(e) => setFeld("abrechnungsjahr", e.target.value)}
                                   data-testid="abrechnung-jahr" />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Fällig im Monat</Label>
                            <Input type="month" value={f.period}
                                   onChange={(e) => setFeld("period", e.target.value)}
                                   data-testid="abrechnung-period" />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-500">Fällig am</Label>
                            <Input type="date" value={f.due_date}
                                   onChange={(e) => setFeld("due_date", e.target.value)}
                                   data-testid="abrechnung-faellig" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs text-gray-500">Notiz</Label>
                        <Textarea rows={2} value={f.notes}
                                  onChange={(e) => setFeld("notes", e.target.value)}
                                  placeholder="woher der Betrag stammt" />
                    </div>
                    {vorschau?.entwurf && (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm space-y-1"
                             data-testid="abrechnung-vorschau">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Mieter</span>
                                <b>{vorschau.mieter}</b>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Betrag</span>
                                <b className="tabular-nums">{euro(vorschau.entwurf.soll_total)}</b>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Fällig</span>
                                <b>{germanDate(vorschau.entwurf.due_date)}</b>
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={pruefen} disabled={busy || fehlt}
                            data-testid="abrechnung-pruefen">
                        Prüfen
                    </Button>
                    <Button className="bg-slate-900 hover:bg-slate-800" onClick={schreiben}
                            disabled={busy || fehlt} data-testid="abrechnung-anlegen">
                        Anlegen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const SollDialog = ({ period, open, onOpenChange, onDone }) => {
    const [vorschau, setVorschau] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) { setVorschau(null); return; }
        setBusy(true);
        generateCharges(period, false).then(setVorschau).finally(() => setBusy(false));
    }, [open, period]);

    const schreiben = async () => {
        setBusy(true);
        try {
            const r = await generateCharges(period, true);
            toast.success(`${r.neu} Soll-Positionen angelegt (${euro(r.soll_summe_neu)})`);
            onDone();
            onOpenChange(false);
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Soll-Positionen für {period}</DialogTitle>
                    <DialogDescription>
                        Aus den aktiven Mietverhältnissen. Vorhandene Positionen bleiben
                        unangetastet — ihr Betrag ist eingefroren und kann korrigiert worden sein.
                    </DialogDescription>
                </DialogHeader>
                {!vorschau ? (
                    <div className="py-8 text-center text-sm text-gray-400">Wird geprüft …</div>
                ) : (
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Aktive Mietverhältnisse</span>
                            <b className="tabular-nums">{vorschau.aktive_mietverhaeltnisse}</b></div>
                        <div className="flex justify-between"><span className="text-gray-500">Neu anzulegen</span>
                            <b className="tabular-nums">{vorschau.neu}</b></div>
                        <div className="flex justify-between"><span className="text-gray-500">Bereits vorhanden</span>
                            <b className="tabular-nums">{vorschau.bereits_vorhanden}</b></div>
                        <div className="flex justify-between border-t border-gray-100 pt-2">
                            <span className="text-gray-500">Soll-Summe neu</span>
                            <b className="tabular-nums">{euro(vorschau.soll_summe_neu)}</b></div>
                        {vorschau.ohne_miete_uebersprungen?.length > 0 && (
                            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs">
                                <b className="text-amber-900">
                                    {vorschau.ohne_miete_uebersprungen.length} ohne Miete übersprungen
                                </b>
                                <div className="text-amber-800 mt-1">
                                    {vorschau.ohne_miete_uebersprungen.map((t) => t.name || t.tenant_id).join(", ")}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button onClick={schreiben} disabled={busy || !vorschau?.neu}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="soll-schreiben">
                        {vorschau?.neu ? `${vorschau.neu} anlegen` : "Nichts anzulegen"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Zuordnung vorschlagen — Trockenlauf zuerst, wie überall in diesem Projekt
// ---------------------------------------------------------------------------
// Absichtlich ohne Periode: eine Zahlung darf einen alten Rückstand tilgen, und
// genau das ist die übliche Tilgungsreihenfolge. Wer nur den aktuellen Monat
// zuordnet, lässt Rückstände für immer stehen.
const ZuordnungDialog = ({ open, onOpenChange, onDone }) => {
    const [vorschau, setVorschau] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) { setVorschau(null); return; }
        setBusy(true);
        proposeAllocations(null, false).then(setVorschau).finally(() => setBusy(false));
    }, [open]);

    const schreiben = async () => {
        setBusy(true);
        try {
            const r = await proposeAllocations(null, true);
            toast.success(`${r.davon_geschrieben} Zuordnungen vorgeschlagen (${euro(r.summe_vorschlaege)})`,
                          { description: "Noch nicht bestätigt — die Ampel bleibt rot, bis du sie prüfst." });
            onDone();
            onOpenChange(false);
        } finally { setBusy(false); }
    };

    const sicher = vorschau?.vorschlaege?.filter((v) => v.sicherheit >= 0.9).length ?? 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Zahlungen zuordnen</DialogTitle>
                    <DialogDescription>
                        Abgeleitet aus der Konto-IBAN und dem Abstand zur Fälligkeit. Wird als
                        Vorschlag angelegt, nicht als Bestätigung — grün wird eine Position erst,
                        wenn du sie bestätigt hast.
                    </DialogDescription>
                </DialogHeader>
                {!vorschau ? (
                    <div className="py-8 text-center text-sm text-gray-400">Wird geprüft …</div>
                ) : (
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Geprüfte Eingänge</span>
                            <b className="tabular-nums">{vorschau.geprueft_zahlungen}</b></div>
                        <div className="flex justify-between"><span className="text-gray-500">Offene Soll-Positionen</span>
                            <b className="tabular-nums">{vorschau.offene_positionen}</b></div>
                        <div className="flex justify-between"><span className="text-gray-500">Vorschläge</span>
                            <b className="tabular-nums">{vorschau.anzahl_vorschlaege}</b></div>
                        <div className="flex justify-between"><span className="text-gray-500">Davon eindeutig (eigenes Konto)</span>
                            <b className="tabular-nums">{sicher}</b></div>
                        <div className="flex justify-between border-t border-gray-100 pt-2">
                            <span className="text-gray-500">Summe</span>
                            <b className="tabular-nums">{euro(vorschau.summe_vorschlaege)}</b></div>

                        {vorschau.vorschlaege?.length > 0 && (
                            <div className="mt-3 max-h-64 overflow-y-auto border border-gray-100 rounded-md">
                                <table className="w-full text-xs">
                                    <tbody>
                                    {vorschau.vorschlaege.map((v, i) => (
                                        <tr key={i} className="border-b border-gray-50 last:border-0">
                                            <td className="px-2 py-1.5 tabular-nums text-right w-24">{euro(v.betrag)}</td>
                                            <td className="px-2 py-1.5 text-gray-500 w-20">{v.period}</td>
                                            <td className="px-2 py-1.5">{v.mieter || v.einheit || "—"}</td>
                                            <td className="px-2 py-1.5 text-gray-400 text-right w-20">
                                                {v.tage_zur_faelligkeit}&nbsp;Tage
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {vorschau.ohne_treffer?.length > 0 && (
                            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs">
                                <b className="text-amber-900">
                                    {vorschau.ohne_treffer.length} Eingänge ohne Vorschlag
                                </b>
                                <div className="text-amber-800 mt-1">
                                    Meist zahlt jemand anders als der Mieter, oder das Konto gehört
                                    einer Immobilie mit mehreren Mietverhältnissen. Diese bleiben
                                    Handarbeit — ein falsch zugeordneter Eingang ist teurer als ein
                                    offener.
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button onClick={schreiben} disabled={busy || !vorschau?.anzahl_vorschlaege}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="zuordnung-schreiben">
                        {vorschau?.anzahl_vorschlaege
                            ? `${vorschau.anzahl_vorschlaege} vorschlagen`
                            : "Nichts zuzuordnen"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Abweichungen: Soll gegen Ist ueber ein Jahr
// ---------------------------------------------------------------------------
// Zwei Euro zu wenig fallen in keinem einzelnen Monat auf. Ueber zwoelf Monate
// sind es vierundzwanzig, und nach drei Jahren sucht sie niemand mehr
// zusammen. Deshalb eine eigene Ansicht mit einer Jahreszahl statt eines
// Monats -- und mit dem Vorzeichen des Mieterkontos: positiv heisst, es ist
// mehr gekommen als gefordert.
const AbweichungsAnsicht = () => {
    const [jahr, setJahr] = useState(new Date().getFullYear());
    const [daten, setDaten] = useState(null);
    const [busy, setBusy] = useState(false);
    const [offen, setOffen] = useState({});

    const laden = useCallback(async () => {
        setBusy(true);
        try {
            setDaten(await mietkontoAbweichungen(jahr));
        } catch {
            toast.error("Abweichungen konnten nicht geladen werden");
        } finally { setBusy(false); }
    }, [jahr]);
    useEffect(() => { laden(); }, [laden]);

    const jahre = daten?.jahre?.length ? daten.jahre : [String(jahr)];
    const zeilen = daten?.zeilen || [];
    const mitAbweichung = zeilen.filter((z) => Math.abs(z.differenz) > 0.005);

    return (
        <>
            <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-xs text-gray-500">Jahr</Label>
                <SuchAuswahl wert={String(jahr)} onWaehlen={(v) => setJahr(Number(v))}
                    optionen={[
                        ...jahre.map((j) => ({ wert: String(j), text: j })),
                    ]}
                    className="w-[110px]"
                    testId="abweichung-jahr"
                />
                <Button variant="outline" size="sm" className="h-8" onClick={laden} disabled={busy}>
                    <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
                </Button>
                <span className="text-[11px] text-gray-500 ml-2">
                    Gezählt wird nur bestätigt Zugeordnetes. Plus heißt: mehr gezahlt als gefordert.
                </span>
                <span className="ml-auto text-xs text-gray-500" data-testid="abweichung-anzahl">
                    {mitAbweichung.length} von {zeilen.length} Mietverhältnissen weichen ab
                </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kachel label="Soll im Jahr" wert={euro(daten?.soll_summe ?? 0)}
                        fuss={`${jahr}`} testid="kachel-abw-soll" />
                <Kachel label="Bestätigt gezahlt" wert={euro(daten?.ist_summe ?? 0)}
                        fuss="nur bestätigte Zuordnungen" testid="kachel-abw-ist" />
                <Kachel label="Saldo" ton={(daten?.saldo ?? 0) < 0 ? "rot" : "gruen"}
                        wert={euro(daten?.saldo ?? 0)}
                        fuss={`${euro(daten?.zu_viel ?? 0)} zu viel · ${euro(daten?.zu_wenig ?? 0)} zu wenig`}
                        testid="kachel-abw-saldo" />
                <Kachel label="Noch Vorschlag" ton="gelb" wert={euro(daten?.offen_vorschlag ?? 0)}
                        fuss="wartet auf Bestätigung" testid="kachel-abw-vorschlag" />
            </div>

            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                            <th className="text-left font-medium px-4 py-3">Mieter</th>
                            <th className="text-left font-medium px-4 py-3">Einheit</th>
                            <th className="text-right font-medium px-4 py-3">Monate</th>
                            <th className="text-right font-medium px-4 py-3">Soll</th>
                            <th className="text-right font-medium px-4 py-3">Gezahlt</th>
                            <th className="text-right font-medium px-4 py-3">Differenz</th>
                            <th className="text-left font-medium px-4 py-3">Auffällige Monate</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {zeilen.map((z) => {
                            const auf = z.abweichungen || [];
                            const zu = !!offen[z.tenant_id];
                            return (
                                <React.Fragment key={z.tenant_id}>
                                    <tr className="hover:bg-gray-50" data-testid={`abweichung-${z.tenant_id}`}>
                                        <td className="px-4 py-3 font-medium text-slate-900">
                                            {z.mieter || "—"}
                                            <div className="text-[10px] text-gray-400">{z.immobilie || ""}</div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{z.einheit || "—"}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-gray-600">{z.monate}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{euro(z.soll)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{euro(z.ist)}</td>
                                        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
                                            z.differenz < -0.005 ? "text-rose-600"
                                            : z.differenz > 0.005 ? "text-emerald-600" : "text-gray-400"}`}>
                                            {euro(z.differenz)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {auf.length === 0 ? (
                                                <span className="text-xs text-gray-400">—</span>
                                            ) : (
                                                <button type="button"
                                                        className="text-xs text-slate-700 underline underline-offset-2"
                                                        data-testid={`abweichung-monate-${z.tenant_id}`}
                                                        onClick={() => setOffen((s) => ({ ...s, [z.tenant_id]: !zu }))}>
                                                    {auf.length} Monat{auf.length === 1 ? "" : "e"}
                                                    {zu ? " ausblenden" : " zeigen"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {zu && auf.map((a) => (
                                        <tr key={`${z.tenant_id}-${a.period}`} className="bg-slate-50/50 text-xs">
                                            <td className="px-4 py-1.5 text-gray-400" />
                                            <td className="px-4 py-1.5 text-gray-500 font-mono">{a.period}</td>
                                            <td />
                                            <td className="px-4 py-1.5 text-right tabular-nums text-gray-600">{euro(a.soll)}</td>
                                            <td className="px-4 py-1.5 text-right tabular-nums text-gray-600">{euro(a.ist)}</td>
                                            <td className={`px-4 py-1.5 text-right tabular-nums ${
                                                a.differenz < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                                                {euro(a.differenz)}
                                            </td>
                                            <td className="px-4 py-1.5 text-gray-500">
                                                {a.vorschlag > 0.005
                                                    ? `${euro(a.vorschlag)} liegen als Vorschlag bereit`
                                                    : ""}
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        {zeilen.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-gray-400">
                                Für {jahr} gibt es keine Soll-Positionen.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};


// ---------------------------------------------------------------------------
// Einen einzelnen Zuordnungsvorschlag pruefen
// ---------------------------------------------------------------------------
// Haken und Kreuz stehen seit dem 10.09.2026 in der Zeile, und fuer die
// eindeutigen Faelle reicht das. Fuer die uebrigen nicht: "+ 225,00 EUR am
// 14.07.2026" sagt nicht, WOFUER die 225 EUR ueberwiesen wurden. Das sagt der
// Verwendungszweck -- und der stand bisher nirgends in der Oberflaeche.
//
// Darum diese Zwischenstufe: ein Klick auf die Zeile zeigt die Buchung so, wie
// sie auf dem Kontoauszug steht, daneben die Forderung, der sie zugeschlagen
// werden soll. Entschieden wird hier genauso wie in der Zeile.
const PruefZeile = ({ label, children, stark = false }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-gray-100 last:border-0">
        <span className="text-xs text-gray-500 shrink-0">{label}</span>
        <span className={`text-sm text-right ${stark ? "font-semibold text-slate-900" : "text-slate-700"}`}>
            {children ?? "\u2014"}
        </span>
    </div>
);

const VorschlagPruefDialog = ({ buchung, charge, open, onOpenChange, onEntscheiden }) => {
    const [laeuft, setLaeuft] = useState(false);
    useEffect(() => { if (open) setLaeuft(false); }, [open]);
    if (!buchung) return null;
    const entscheiden = async (annehmen) => {
        setLaeuft(true);
        await onEntscheiden(annehmen);
    };
    // Der zugeordnete Teil ist oft nur ein Stueck der Ueberweisung: 1.225 EUR
    // kommen herein, 650 davon decken diesen Monat. Beides zu zeigen ist der
    // Unterschied zwischen "stimmt" und "stimmt vielleicht".
    const teil = Math.abs((buchung.zahlung_gesamt || 0) - (buchung.betrag || 0)) > 0.005;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="vorschlag-pruefen">
                <DialogHeader>
                    <DialogTitle>Vorschlag pr\u00fcfen</DialogTitle>
                    <DialogDescription>
                        Geh\u00f6rt diese Zahlung zu dieser Forderung? Der Verwendungszweck
                        sagt es meist genauer als Betrag und Datum.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1">Die Buchung</div>
                        <PruefZeile label="Betrag auf dem Auszug" stark>
                            {euro(buchung.zahlung_gesamt || buchung.betrag)}
                        </PruefZeile>
                        {teil && (
                            <PruefZeile label="davon dieser Forderung zugeordnet" stark>
                                {euro(buchung.betrag)}
                            </PruefZeile>
                        )}
                        <PruefZeile label="Buchungstag">{germanDate(buchung.buchungsdatum)}</PruefZeile>
                        {buchung.valuta && buchung.valuta !== buchung.buchungsdatum && (
                            <PruefZeile label="Wertstellung">{germanDate(buchung.valuta)}</PruefZeile>
                        )}
                        <PruefZeile label="Einzahler">{buchung.einzahler}</PruefZeile>
                        <PruefZeile label="Konto">{buchung.konto}</PruefZeile>
                        <PruefZeile label="Verwendungszweck">
                            <span className="break-words">{buchung.verwendungszweck}</span>
                        </PruefZeile>
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1">Die Forderung</div>
                        <PruefZeile label="Mieter">{charge?.tenant_name}</PruefZeile>
                        <PruefZeile label="Einheit">
                            {[charge?.immobilie, charge?.unit_label].filter(Boolean).join(" \u00b7 ") || null}
                        </PruefZeile>
                        <PruefZeile label="Monat">{charge?.period}</PruefZeile>
                        <PruefZeile label="Soll" stark>{euro(charge?.soll)}</PruefZeile>
                        <PruefZeile label="davon noch offen">{euro(charge?.rest_offen ?? charge?.offen)}</PruefZeile>
                    </div>

                    {buchung.begruendung && (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400 mb-1">
                                Warum vorgeschlagen
                            </div>
                            <div className="text-xs text-gray-700">{buchung.begruendung}</div>
                            {typeof buchung.sicherheit === "number" && (
                                <div className="text-[11px] text-gray-500 mt-1">
                                    Sicherheit {Math.round(buchung.sicherheit * 100)} %
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" disabled={laeuft}
                            onClick={() => entscheiden(false)}
                            data-testid="vorschlag-ablehnen">
                        Ablehnen
                    </Button>
                    <Button disabled={laeuft}
                            className="bg-slate-900 hover:bg-slate-800"
                            onClick={() => entscheiden(true)}
                            data-testid="vorschlag-bestaetigen">
                        Bestätigen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Mietkonto = () => {
    const [period, setPeriod] = useState(heuteMonat());
    const [charges, setCharges] = useState([]);
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [gesamt, setGesamt] = useState(null);      // Rückstände über alle Monate
    const [ampelFilter, setAmpelFilter] = useState("");
    const [tab, setTab] = useState("monat");
    const [busy, setBusy] = useState(false);

    const [klaerungFor, setKlaerungFor] = useState(null);
    const [zuordnenFor, setZuordnenFor] = useState(null);
    const [zahlungOffen, setZahlungOffen] = useState(false);
    const [sollOffen, setSollOffen] = useState(false);

    const [zuordnungOffen, setZuordnungOffen] = useState(false);
    const [abrechnungOffen, setAbrechnungOffen] = useState(false);
    const { gesperrt, grund } = useSchreibschutz();
    // Filter und Sortierung -- je Reiter ein eigener Satz. Bei 42 Positionen
    // ginge es noch ohne, bei 86 Einheiten, 28 Rueckstandsmietern und
    // vierstelligen Zahlungslisten nicht mehr.
    const satzMonat = useFilterSatz();
    const satzRueck = useFilterSatz();
    const satzZahlung = useFilterSatz();
    const [sortierung, setSortierung] = useState("offen_ab");
    const [sortRueck, setSortRueck] = useState("offen_ab");
    const [sortZahlung, setSortZahlung] = useState("datum_ab");
    // Zusaetzlich im Zahlungsreiter: Filter nach Konto. 137 der 218 Zahlungen
    // haengen an Mietkonten OHNE Zuordnung (die Sammel- und Entitaetskonten) --
    // fuer die ist der Immobilienfilter leer, der Kontoname aber da.
    const [kontoFilter, setKontoFilter] = useState("alle");
    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [c, p, s, g] = await Promise.all([
                listCharges({ period }),
                listRentPayments({}),
                mietkontoSummary(period),
                mietkontoSummary(),
            ]);
            setCharges(c); setPayments(p); setSummary(s); setGesamt(g);
        } catch {
            toast.error("Mietkonto konnte nicht geladen werden");
        } finally { setBusy(false); }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    // Vorschlag annehmen oder ablehnen, direkt aus der Monatszeile.
    //
    // Warum in der Zeile und nicht nur im Dialog der Position: "Zahlungen
    // zuordnen" schreibt die Vorschlaege in einem Rutsch -- 18 auf einmal --,
    // und ein Vorschlag zaehlt erst nach der Bestaetigung gegen das Soll. Ohne
    // diesen Griff muesste man 18 Dialoge oeffnen. Und bis zum 10.09.2026 gab
    // es die Bestaetigung in der Oberflaeche ueberhaupt nicht: anlegen ging,
    // verwerfen ging, gruen werden nicht.
    // Welcher Vorschlag gerade geprueft wird: {buchung, charge}
    const [pruefung, setPruefung] = useState(null);

    const vorschlagEntscheiden = useCallback(async (allocId, annehmen, charge) => {
        try {
            if (annehmen) await confirmAllocation(allocId);
            else await deleteAllocation(allocId);
            toast.success(annehmen ? "Bestätigt — zählt jetzt gegen das Soll"
                                   : "Vorschlag abgelehnt",
                          { description: charge?.tenant_name || undefined });
        } catch {
            toast.error("Hat nicht geklappt");
        }
        await load();
    }, [load]);

    // Eine geänderte Position an ihrer Stelle ersetzen, statt alles neu zu laden —
    // sonst springt die Liste nach jedem Klick.
    const ersetze = (frisch) => {
        setCharges((alt) => alt.map((c) => (c.id === frisch.id ? { ...c, ...frisch } : c)));
        mietkontoSummary(period).then(setSummary).catch(() => {});
        mietkontoSummary().then(setGesamt).catch(() => {});
        listRentPayments({}).then(setPayments).catch(() => {});
    };

    const { query, setQuery, filtered, total } = useFulltextFilter(charges, SUCHE_MONAT);
    const rueckstaende = useMemo(() => gesamt?.rueckstaende || [], [gesamt]);
    const suchRueck = useFulltextFilter(rueckstaende, SUCHE_RUECKSTAENDE);
    const suchZahlung = useFulltextFilter(payments, SUCHE_ZAHLUNGEN);

    const auswahl = useMemo(() => auswahlAus(charges), [charges]);
    const auswahlRueck = useMemo(() => auswahlAus(rueckstaende), [rueckstaende]);
    const auswahlZahlung = useMemo(() => ({
        ...auswahlAus(payments),
        konten: [...new Set(payments.map((z) => z.konto).filter(Boolean))].sort(),
    }), [payments]);


    // Kopie sortieren: an der Stelle sortieren wuerde das Ergebnis des
    // Filter-useMemo veraendern und beim naechsten Rendern eine andere
    // Reihenfolge liefern, ohne dass sich etwas geaendert hat.
    const sichtbar = useMemo(() => {
        // "abweichung" ist kein Ampelwert, sondern die Frage der
        // Mieteingangskontrolle: wo stimmt der Eingang nicht mit dem Soll
        // ueberein -- zu wenig ODER zu viel.
        //
        // Der Name ist mit Bedacht NICHT "passt": so heisst der Filter fuer
        // Entitaet, Immobilie und Typ eine Zeile weiter unten. Die erste
        // Fassung hiess so und hat ihn ueberdeckt -- danach war die
        // Monatsliste leer ("0 von 3 Positionen"), weil der Zustandsfilter
        // mit leerem Wert auf jede Zeile "nein" sagte.
        const passtZustand = (c) => (ampelFilter === "abweichung"
            ? (c.offen > 0.005 || c.ueberzahlung > 0.005)
            : c.ampel === ampelFilter);
        const liste = (ampelFilter ? filtered.filter(passtZustand) : filtered)
            .filter((c) => passt(c, satzMonat.werte));
        return [...liste].sort(SORTIERUNGEN[sortierung]?.cmp || (() => 0));
    }, [filtered, ampelFilter, satzMonat.werte, sortierung]);

    const sichtbarRueck = useMemo(() => {
        const liste = suchRueck.filtered.filter((r) => passt(r, satzRueck.werte));
        return [...liste].sort(SORT_RUECKSTAENDE[sortRueck]?.cmp || (() => 0));
    }, [suchRueck.filtered, satzRueck.werte, sortRueck]);

    const sichtbarZahlungen = useMemo(() => {
        const liste = suchZahlung.filtered.filter(
            (z) => passt(z, satzZahlung.werte)
                && (kontoFilter === "alle" || z.konto === kontoFilter));
        return [...liste].sort(SORT_ZAHLUNGEN[sortZahlung]?.cmp || (() => 0));
    }, [suchZahlung.filtered, satzZahlung.werte, kontoFilter, sortZahlung]);

    const offeneZahlungen = useMemo(() => payments.filter((p) => p.rest > 0.005 && p.amount > 0), [payments]);

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Betrieb</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="mietkonto-title">
                        Mietkonto
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                        Soll gegen Ist. Eine Abweichung mit begründeter Klärung verlässt die
                        Arbeitsliste — offen bleibt nur, was wirklich offen ist.
                    </p>
                </div>
                <div className="flex items-end gap-2">
                    <div>
                        <Label className="text-[11px] text-gray-500">Monat</Label>
                        <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                               className="w-[150px]" data-testid="mietkonto-monat" />
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={busy} className="h-9">
                        <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
                    </Button>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-9"
                            onClick={() => setSollOffen(true)} data-testid="btn-soll-erzeugen"
                            disabled={gesperrt} title={grund}>
                        <Plus size={14} className="mr-1.5" /> Soll erzeugen
                    </Button>
                    <Button size="sm" variant="outline" className="h-9"
                            onClick={() => setZuordnungOffen(true)} data-testid="btn-zuordnung-vorschlagen"
                            disabled={gesperrt} title={grund}>
                        <Link2 size={14} className="mr-1.5" /> Zahlungen zuordnen
                    </Button>
                    <Button size="sm" variant="outline" className="h-9"
                            onClick={() => setAbrechnungOffen(true)} data-testid="btn-abrechnung"
                            disabled={gesperrt} title={grund}>
                        <Scale size={14} className="mr-1.5" /> Abrechnung erfassen
                    </Button>
                </div>
            </div>

            {/* Kennzahlen */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <Kachel label="Soll im Monat" wert={euro(summary?.soll_summe ?? 0)}
                        fuss={`${summary?.positionen ?? 0} Positionen`} testid="kachel-soll" />
                <Kachel label="Offen" ton="rot" wert={euro(summary?.offen_rot ?? 0)}
                        fuss="unerklärt und fällig" testid="kachel-offen" />
                <Kachel label="Erklärt offen" ton="gelb" wert={euro(summary?.offen_gelb ?? 0)}
                        fuss="gestundet, in Klärung, noch nicht fällig" testid="kachel-erklaert" />
                <Kachel label="Erledigt" ton="gruen" wert={summary?.ampel?.gruen ?? 0}
                        fuss={`von ${summary?.positionen ?? 0} Positionen`} testid="kachel-erledigt" />
                {/* Zu viel gezahlt ist genauso eine Abweichung wie zu wenig --
                    nur faellt sie nicht auf, weil die Position gruen wird.
                    Gemeldet am 11.09.2026 an der Familie Passenheim: 1.230 EUR
                    ueberwiesen, 1.180 EUR gefordert, in der Monatsansicht
                    nicht zu sehen. */}
                <Kachel label="Mehr gezahlt" ton={summary?.ueberzahlt ? "gelb" : "neutral"}
                        wert={euro(summary?.ueberzahlt ?? 0)}
                        fuss={`${summary?.ueberzahlt_positionen ?? 0} Position${
                            (summary?.ueberzahlt_positionen ?? 0) === 1 ? "" : "en"} betroffen`}
                        testid="kachel-ueberzahlt" />
                <Kachel label="Rückstände gesamt" ton={gesamt?.offen_rot ? "rot" : "neutral"}
                        wert={euro(gesamt?.offen_rot ?? 0)}
                        fuss={`${gesamt?.rueckstaende?.length ?? 0} Mieter über alle Monate`}
                        testid="kachel-rueckstaende" />
            </div>

            {/* Abrechnungen stehen neben der Mietampel, nicht darin: eigene
                Faelligkeit, eigene Forderung. Die beiden Summen heben sich
                nicht gegenseitig auf -- offene Forderung und offene
                Erstattung sind zwei verschiedene Arbeitsvorraete. */}
            {(summary?.abrechnungen?.offen?.length ?? 0) > 0 && (
                <div className="bg-white border border-violet-200 rounded-md overflow-hidden"
                     data-testid="abrechnungen-offen">
                    <div className="px-5 py-3 bg-violet-50 flex items-start gap-2">
                        <Scale size={15} className="text-violet-700 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-medium text-slate-900">
                                Offene Abrechnungen · {summary.abrechnungen.offen.length}
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                {euro(summary.abrechnungen.forderung_offen)} vom Mieter einzuziehen
                                {Math.abs(summary.abrechnungen.erstattung_offen) > 0.005 && (
                                    <> · {euro(Math.abs(summary.abrechnungen.erstattung_offen))} zu erstatten</>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {summary.abrechnungen.offen.map((a) => (
                            <div key={a.id} className="px-5 py-2 flex items-baseline gap-3 text-xs"
                                 data-testid={`abrechnung-${a.id}`}>
                                <span className="text-gray-400 w-16 shrink-0">{a.period}</span>
                                <span className="text-slate-800 truncate flex-1">
                                    {a.tenant_id
                                        ? <Link to={`/mieter?edit=${a.tenant_id}`} className="hover:underline">
                                              {a.tenant_name}
                                          </Link>
                                        : a.tenant_name}
                                    {a.immobilie && <span className="text-gray-400">
                                        {" · "}
                                        {a.property_id
                                            ? <Link to={`/immobilien/${a.property_id}`} className="hover:underline">
                                                  {a.immobilie}
                                              </Link>
                                            : a.immobilie}
                                    </span>}
                                    {a.unit_label && <span className="text-gray-400">
                                        {" · "}
                                        {a.unit_id
                                            ? <Link to={`/einheiten/${a.unit_id}`} className="hover:underline">
                                                  {a.unit_label}
                                              </Link>
                                            : a.unit_label}
                                    </span>}
                                </span>
                                {a.abrechnungsjahr && (
                                    <span className="text-gray-400 shrink-0">Abrechnung {a.abrechnungsjahr}</span>
                                )}
                                <span className="text-gray-400 w-20 text-right shrink-0">
                                    {germanDate(a.due_date)}
                                </span>
                                <span className={`tabular-nums w-24 text-right shrink-0 ${
                                    a.soll < 0 ? "text-violet-700" : "text-slate-900"}`}>
                                    {euro(a.offen)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-gray-100 p-1 rounded-md">
                    <TabsTrigger value="monat" data-testid="tab-monat">
                        <Wallet size={14} className="mr-1.5" /> Monat
                    </TabsTrigger>
                    <TabsTrigger value="rueckstaende" data-testid="tab-rueckstaende">
                        <AlertCircle size={14} className="mr-1.5" /> Rückstände
                    </TabsTrigger>
                    <TabsTrigger value="zahlungen" data-testid="tab-zahlungen">
                        <Link2 size={14} className="mr-1.5" /> Zahlungen
                    </TabsTrigger>
                    {/* Die Monatsansicht sagt "ist dieser Monat bezahlt".
                        Die Frage dahinter ist: wer weicht dauerhaft ab, und
                        um wie viel im Jahr. */}
                    <TabsTrigger value="abweichungen" data-testid="tab-abweichungen">
                        <Scale size={14} className="mr-1.5" /> Abweichungen
                    </TabsTrigger>
                </TabsList>

                {/* ---------------- Monat ---------------- */}
                <TabsContent value="monat" className="mt-5 space-y-3">
                    {/* Ein Monat sieht sauber aus, wenn jede Position gedeckt ist --
                        auch dann, wenn die Haelfte der Eingaenge noch niemandem
                        zugeordnet ist. Diese Zeile schliesst die Luecke. */}
                    {summary?.eingaenge_ohne_zuordnung?.anzahl > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2 flex-wrap"
                             data-testid="hinweis-ohne-zuordnung">
                            <AlertCircle size={14} className="shrink-0" />
                            <span>
                                <b>{summary.eingaenge_ohne_zuordnung.anzahl} Eingänge über{" "}
                                {euro(summary.eingaenge_ohne_zuordnung.summe)}</b> sind noch keiner
                                Position zugeordnet — solange sie das nicht sind, fehlen sie hier.
                                {summary.eingaenge_ohne_zuordnung.zeitraum && (
                                    <span className="text-amber-800">
                                        {" "}(Buchungen vom{" "}
                                        {germanDate(summary.eingaenge_ohne_zuordnung.zeitraum.von)} bis{" "}
                                        {germanDate(summary.eingaenge_ohne_zuordnung.zeitraum.bis)})
                                    </span>
                                )}
                            </span>
                            <Button size="sm" variant="outline" className="h-7 ml-auto"
                                    onClick={() => setZuordnungOffen(true)}
                                    disabled={gesperrt} title={grund}
                                    data-testid="btn-zuordnen-aus-hinweis">
                                <Link2 size={13} className="mr-1.5" /> Zahlungen zuordnen
                            </Button>
                        </div>
                    )}
                    <FilterLeiste prefix="mietkonto" query={query} setQuery={setQuery}
                                  platzhalter="Suche Mieter, Einheit, Immobilie, Grund…"
                                  auswahl={auswahl} satz={satzMonat}
                                  sortierungen={SORTIERUNGEN} sortierung={sortierung}
                                  setSortierung={setSortierung}
                                  matches={sichtbar.length} total={total}
                                  label="Positionen" labelSingular="Position"
                                  extraAktiv={!!ampelFilter}
                                  extraZuruecksetzen={() => setAmpelFilter("")}>
                        <Select value={ampelFilter || "all"}
                                onValueChange={(v) => setAmpelFilter(v === "all" ? "" : v)}>
                            <SelectTrigger className="w-[190px]" data-testid="filter-ampel">
                                <SelectValue placeholder="Alle Zustände" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle Zustände</SelectItem>
                                <SelectItem value="rot">Nur offen</SelectItem>
                                <SelectItem value="gelb">Nur in Arbeit</SelectItem>
                                <SelectItem value="gruen">Nur erledigt</SelectItem>
                                {/* Kurz halten: der lange Text stand 85 px ueber dem Feld --
                                    dieselbe Falle wie bei den Anlaessen der
                                    Mietanpassung. */}
                                <SelectItem value="abweichung"
                                            title="Zeigt jede Position, bei der Eingang und Soll auseinandergehen — zu wenig oder zu viel">
                                    Nur Abweichungen
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </FilterLeiste>

                    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-left">
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Immobilie · Einheit</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Soll</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Bezahlt</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Offen</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Zustand</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Begründung</th>
                                    <th className="px-4 py-2.5" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sichtbar.map((c) => (
                                    <tr key={c.id} className="hover:bg-gray-50/60" data-testid={`charge-${c.id}`}>
                                        {/* Immobilie und Einheit zuerst, Mieter darunter: die
                                            Einheit bleibt, Mieter wechseln. Wer eine Wohnung
                                            sucht, sucht nach der Adresse — nicht nach dem, der
                                            gerade darin wohnt. */}
                                        <td className="px-4 py-2.5">
                                            <EinheitZelle immobilie={c.immobilie} einheit={c.unit_label}
                                                          zusatz={`${c.tenant_name || "—"} · fällig ${germanDate(c.due_date)}`} />
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{euro(c.soll)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-600">
                                            {euro(c.bezahlt)}
                                            {/* Mehr ueberwiesen als gefordert: genau hier muss es
                                                stehen, denn hier wird abgehakt. */}
                                            {c.ueberzahlung > 0 && (
                                                <div className="text-[10px] text-amber-700 font-sans"
                                                     data-testid={`ueberzahlung-${c.id}`}
                                                     title={c.ueberzahlung_buchungen.map((b) =>
                                                         `${germanDate(b.buchungsdatum)}: ${euro(b.betrag)} überwiesen, davon ${euro(b.rest)} nicht zugeordnet`).join("\n")}>
                                                    + {euro(c.ueberzahlung)} mehr überwiesen
                                                </div>
                                            )}
                                            {c.zugeordnet_vorschlag > 0 && (
                                                <div className="text-[10px] text-amber-600">
                                                    {/* Datum dazu: erst es sagt, ob die Zahlung zu
                                                        diesem Monat gehoert. Miete wird Ende des
                                                        Vormonats ueberwiesen. */}
                                                    {/* Annehmen oder ablehnen direkt in der Zeile.
                                                        Sonst muesste man fuer jeden Vorschlag den
                                                        Dialog der Position oeffnen — bei 18 Stueck
                                                        ist das der Unterschied zwischen zwei Minuten
                                                        und einer Viertelstunde. */}
                                                    {(c.vorschlag_buchungen || []).map((b, i) => (
                                                        <div key={b.id || i} title={b.einzahler || undefined}
                                                             className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                                                            <button type="button"
                                                                    className="hover:underline underline-offset-2 decoration-dotted"
                                                                    title="Buchung ansehen und entscheiden"
                                                                    data-testid={`zeile-pruefen-${b.id}`}
                                                                    onClick={() => setPruefung({ buchung: b, charge: c })}>
                                                                + {euro(b.betrag)} am {germanDate(b.buchungsdatum)}
                                                            </button>
                                                            {b.id && !gesperrt && (
                                                                <>
                                                                    <button type="button"
                                                                            className="text-emerald-700 hover:text-emerald-900 leading-none"
                                                                            title="Vorschlag bestätigen"
                                                                            data-testid={`zeile-bestaetigen-${b.id}`}
                                                                            onClick={() => vorschlagEntscheiden(b.id, true, c)}>
                                                                        <Check size={12} />
                                                                    </button>
                                                                    <button type="button"
                                                                            className="text-gray-400 hover:text-rose-700 leading-none"
                                                                            title="Vorschlag ablehnen"
                                                                            data-testid={`zeile-ablehnen-${b.id}`}
                                                                            onClick={() => vorschlagEntscheiden(b.id, false, c)}>
                                                                        <X size={12} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {!(c.vorschlag_buchungen || []).length &&
                                                        <>+ {euro(c.zugeordnet_vorschlag)} Vorschlag</>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold">
                                            {euro(c.rest_offen ?? c.offen)}
                                        </td>
                                        <td className="px-4 py-2.5"><AmpelPunkt ampel={c.ampel} title={c.grund} /></td>
                                        <td className="px-4 py-2.5 max-w-[260px]">
                                            <div className="text-xs text-gray-600 truncate" title={c.grund}>{c.grund}</div>
                                        </td>
                                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                            {(c.rest_offen ?? c.offen) > 0.005 && (
                                                <Button variant="ghost" size="sm" className="h-8 px-2"
                                                        onClick={() => setZuordnenFor(c)}
                                                        data-testid={`btn-zuordnen-${c.id}`} title="Zahlung zuordnen">
                                                    <Link2 size={14} />
                                                </Button>
                                            )}
                                            <Button variant="ghost" size="sm" className="h-8 px-2"
                                                    onClick={() => setKlaerungFor(c)}
                                                    data-testid={`btn-klaerung-${c.id}`} title="Abweichung begründen">
                                                <MessageSquareWarning
                                                    size={14}
                                                    className={c.klaerung ? "text-amber-600" : ""} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {sichtbar.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-14 text-center text-sm text-gray-400">
                                            {charges.length === 0
                                                ? <>Für {period} gibt es noch keine Soll-Positionen.
                                                    <div className="mt-1 text-xs">Über „Soll erzeugen" anlegen.</div></>
                                                : "Kein Treffer für Suche und Filter."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* ---------------- Rückstände ---------------- */}
                <TabsContent value="rueckstaende" className="mt-5 space-y-3">
                    <div className="text-sm text-gray-500">
                        Über alle Monate. Erklärte Abweichungen stehen hier nicht — nur was
                        unerklärt und fällig ist.
                    </div>
                    <FilterLeiste prefix="rueck" query={suchRueck.query} setQuery={suchRueck.setQuery}
                                  platzhalter="Suche Mieter, Einheit, Immobilie, Monat…"
                                  auswahl={auswahlRueck} satz={satzRueck}
                                  sortierungen={SORT_RUECKSTAENDE} sortierung={sortRueck}
                                  setSortierung={setSortRueck}
                                  matches={sichtbarRueck.length} total={suchRueck.total}
                                  label="Mieter" labelSingular="Mieter" />
                    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-left">
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Immobilie · Einheit</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Mieter</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Monate</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Offen</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Perioden</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sichtbarRueck.map((r) => (
                                    <tr key={r.tenant_id} className="hover:bg-gray-50/60"
                                        data-testid={`rueckstand-${r.tenant_id}`}>
                                        <td className="px-4 py-2.5">
                                            <EinheitZelle immobilie={r.immobilie} einheit={r.unit_label} />
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-600">{r.tenant_name || "—"}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{r.monate}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold text-rose-600">
                                            {euro(r.offen)}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{r.perioden.join(", ")}</td>
                                    </tr>
                                ))}
                                {sichtbarRueck.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-14 text-center text-sm text-gray-400">
                                            {rueckstaende.length ? (
                                                <>Kein Treffer — {rueckstaende.length} Rückstände sind
                                                   durch Suche oder Filter ausgeblendet.</>
                                            ) : (
                                                <><Check size={20} className="mx-auto mb-2 text-emerald-500" />
                                                  Keine unerklärten Rückstände.</>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* ---------------- Zahlungen ---------------- */}
                <TabsContent value="zahlungen" className="mt-5 space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm text-gray-500">
                            {offeneZahlungen.length} von {payments.length} Zahlungen haben einen
                            freien Restbetrag.
                        </div>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => setZahlungOffen(true)} data-testid="btn-zahlung-erfassen">
                            <Plus size={14} className="mr-1.5" /> Zahlung erfassen
                        </Button>
                    </div>
                    <FilterLeiste prefix="zahlung" query={suchZahlung.query} setQuery={suchZahlung.setQuery}
                                  platzhalter="Suche Absender, Zweck, IBAN, Immobilie…"
                                  auswahl={auswahlZahlung} satz={satzZahlung}
                                  sortierungen={SORT_ZAHLUNGEN} sortierung={sortZahlung}
                                  setSortierung={setSortZahlung}
                                  matches={sichtbarZahlungen.length} total={suchZahlung.total}
                                  label="Zahlungen" labelSingular="Zahlung"
                                  extraAktiv={kontoFilter !== "alle"}
                                  extraZuruecksetzen={() => setKontoFilter("alle")}>
                        <SuchAuswahl wert={kontoFilter} onWaehlen={setKontoFilter}
                            optionen={[
                                { wert: "alle", text: "Alle Konten" },
                                ...auswahlZahlung.konten.map((k) => ({ wert: k, text: k })),
                            ]}
                            className="w-[220px]"
                            testId="zahlung-filter-konto"
                        />
                    </FilterLeiste>

                    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-left">
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Buchungstag</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Immobilie · Einheit</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Konto · Absender</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Verwendungszweck</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Betrag</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Zugeordnet</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500 text-right">Frei</th>
                                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-gray-500">Quelle</th>
                                    {/* Klebt rechts, wie in den anderen Listen: sonst
                                        scrollt der Knopf aus dem Bild. */}
                                    <th className="px-4 py-2.5 sticky right-0 bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sichtbarZahlungen.map((p) => (
                                    <tr key={p.id} className="group hover:bg-gray-50/60" data-testid={`payment-${p.id}`}>
                                        <td className="px-4 py-2.5 whitespace-nowrap">{germanDate(p.booking_date)}</td>
                                        {/* Woher die Einheit kommt, steht in
                                            _zahlungen_anreichern: erst die Zuordnung der Zahlung,
                                            sonst die des Kontos. Fehlt beides, bleibt es leer --
                                            geraten wird hier nicht. */}
                                        <td className="px-4 py-2.5">
                                            <EinheitZelle immobilie={p.immobilie} einheit={p.einheit} />
                                        </td>
                                        {/* IBAN in den Titel statt in eine dritte Zeile: sie
                                            macht jede Zeile hoeher und wird selten gelesen --
                                            der Kontoname sagt dasselbe kuerzer. */}
                                        <td className="px-4 py-2.5" title={p.account_iban || undefined}>
                                            <div className="text-xs text-gray-600">{p.konto || p.account_iban}</div>
                                            <div className="text-xs text-gray-500">{p.counterparty_name || "—"}</div>
                                        </td>
                                        <td className="px-4 py-2.5 max-w-[280px]">
                                            <div className="text-xs text-gray-600 truncate" title={p.reference}>
                                                {p.reference || "—"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{euro(p.amount)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums text-gray-500">
                                            {euro(p.zugeordnet)}
                                        </td>
                                        <td className={`px-4 py-2.5 text-right font-mono tabular-nums font-semibold
                                            ${p.rest > 0.005 ? "text-emerald-700" : "text-gray-400"}`}>
                                            {euro(p.rest)}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">
                                            {p.source}
                                            {p.keine_miete && (
                                                <div className="text-[10px] text-gray-400">keine Miete</div>
                                            )}
                                        </td>
                                        {/* Auf einem Sammelkonto laeuft neben der Miete alles
                                            andere der Entitaet. Ohne diesen Haken stuende in der
                                            Kontrolle dauerhaft eine Millionensumme "nicht
                                            zugeordnet" -- gemessen: 2,6 Mio Darlehen im 08/2026. */}
                                        <td className="px-4 py-2.5 text-right sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                                            <Button variant="ghost" size="sm" className="h-7 px-2"
                                                    disabled={gesperrt}
                                                    data-testid={`keine-miete-${p.id}`}
                                                    title={p.keine_miete
                                                        ? "Doch ein Mieteingang — zurück in die Kontrolle"
                                                        : "Gehört nicht zur Miete (Darlehen, Eigenkapital, Guthaben) — bleibt im Bestand, zählt aber nicht mehr als offener Eingang"}
                                                    onClick={() => zahlungKeineMiete(p.id, !p.keine_miete)
                                                        .then(() => {
                                                            toast.success(p.keine_miete
                                                                ? "Zählt wieder als Mieteingang"
                                                                : "Aus der Mieteingangskontrolle genommen");
                                                            load();
                                                        })
                                                        .catch((e) => toast.error(
                                                            e?.response?.data?.detail?.error
                                                            || "Ging nicht"))}>
                                                {p.keine_miete ? <Eye size={13} /> : <EyeOff size={13} />}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {sichtbarZahlungen.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="px-4 py-14 text-center text-sm text-gray-400">
                                            {payments.length
                                                ? <>Kein Treffer — {payments.length} Zahlungen sind
                                                    durch Suche oder Filter ausgeblendet.</>
                                                : <>Noch keine Zahlungen erfasst.</>}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="abweichungen" className="mt-5 space-y-3">
                    <AbweichungsAnsicht />
                </TabsContent>
            </Tabs>

            <KlaerungDialog charge={klaerungFor} open={!!klaerungFor}
                            onOpenChange={(o) => !o && setKlaerungFor(null)} onSaved={ersetze} />
            <ZuordnenDialog charge={zuordnenFor} open={!!zuordnenFor}
                            onOpenChange={(o) => !o && setZuordnenFor(null)} onSaved={ersetze} />
            <ZahlungDialog open={zahlungOffen} onOpenChange={setZahlungOffen} onSaved={load} />
            <SollDialog period={period} open={sollOffen} onOpenChange={setSollOffen} onDone={load} />
            <AbrechnungDialog period={period} open={abrechnungOffen}
                              onOpenChange={setAbrechnungOffen} onDone={load} />
            <ZuordnungDialog open={zuordnungOffen} onOpenChange={setZuordnungOffen} onDone={load} />
            <VorschlagPruefDialog
                buchung={pruefung?.buchung}
                charge={pruefung?.charge}
                open={!!pruefung}
                onOpenChange={(o) => { if (!o) setPruefung(null); }}
                onEntscheiden={async (annehmen) => {
                    const { buchung, charge } = pruefung;
                    setPruefung(null);
                    await vorschlagEntscheiden(buchung.id, annehmen, charge);
                }} />
        </div>
    );
};

export default Mietkonto;
